import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { orderStatusLabel } from '@telestore/i18n';
import { statusTimeline } from '@telestore/order-core';
import { formatPhnomPenh, provinceName } from '@telestore/shared';
import { botDeepLink } from '@telestore/telegram-adapter/deeplink';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Check } from 'lucide-react';
import { useShop } from '../lib/shop-context.js';
import { haptic, isMiniApp, openTelegramLink, telegramInitData } from '../lib/telegram.js';
import { ShopHeader } from '../components/chrome.js';
import { ErrorNotice, Money, Spinner } from '../components/primitives.js';
import { QrCode } from '../components/qr.js';
import { Button } from '../components/ui/button.js';

/**
 * Order confirmation and tracking.
 *
 * This page carries the single most important interaction in the funnel
 * (§6.3): the Telegram handoff. The customer has just paid or committed to
 * pay, so this is the exact moment where "get updates on Telegram" is a
 * favour rather than an ask — and every tap converts an ad-bought buyer into a
 * free lifetime bot subscriber.
 */
export function OrderPage() {
  const { slug, code } = useParams({ from: '/s/$slug/order/$code' });
  const { data, locale, t } = useShop();
  const shop = data.shop;
  const queryClient = useQueryClient();
  const [linked, setLinked] = useState(false);

  const query = useQuery({
    queryKey: ['order', slug, code],
    queryFn: () => api.order(slug, code),
    // SSE below is the fast path; this is the fallback for a dropped stream.
    refetchInterval: (q) =>
      q.state.data?.order.status === 'awaiting_payment' ? 15_000 : false,
  });

  /**
   * Live payment status. A customer who has just transferred stares at this
   * screen; SSE turns a 15-second poll into an instant flip the moment the ABA
   * listener matches the transfer.
   */
  useEffect(() => {
    if (query.data?.order.status !== 'awaiting_payment') return;
    const stream = api.orderStream(slug, code);
    stream.onmessage = (event) => {
      const payload = JSON.parse(event.data) as { type: string };
      if (payload.type === 'payment.confirmed' || payload.type === 'order.status') {
        haptic('success');
        void queryClient.invalidateQueries({ queryKey: ['order', slug, code] });
      }
    };
    stream.onerror = () => stream.close();
    return () => stream.close();
  }, [query.data?.order.status, slug, code, queryClient]);

  // Inside a Mini App we can complete the handoff silently — the customer is
  // already in Telegram, so there is nothing to ask.
  useEffect(() => {
    if (linked || !isMiniApp()) return;
    const initData = telegramInitData();
    if (!initData) return;
    void api
      .linkTelegram(slug, code, initData)
      .then(() => setLinked(true))
      .catch(() => undefined);
  }, [linked, slug, code]);

  if (query.isPending) {
    return (
      <div className="min-h-dvh">
        <ShopHeader showBack />
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="min-h-dvh">
        <ShopHeader showBack />
        <ErrorNotice
          message={(query.error as Error | undefined)?.message ?? t('errors.not_found')}
          onRetry={() => void query.refetch()}
          retryLabel={t('common.retry')}
        />
      </div>
    );
  }

  const { order, payment } = query.data;
  const timeline = statusTimeline(order.paymentType);
  const currentIndex = timeline.indexOf(order.status);
  const awaitingPayment = order.status === 'awaiting_payment' && payment?.qrPayload;

  return (
    <div className="min-h-dvh pb-16">
      <ShopHeader showBack />

      <div className="flex flex-col gap-6 px-4 pt-6">
        <header className="text-center">
          <p className="text-sm opacity-65">{t('order.title', { code: order.code })}</p>
          <h1 className="mt-1 text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
            {orderStatusLabel(locale, order.status)}
          </h1>
          {order.placedAt ? (
            <p className="mt-1 text-xs opacity-55">
              {t('order.placedAt', { date: formatPhnomPenh(new Date(order.placedAt), locale) })}
            </p>
          ) : null}
        </header>

        {awaitingPayment ? (
          <section className="card flex flex-col items-center gap-3 p-5">
            <p className="text-sm opacity-70">{t('payment.scanToPay')}</p>
            <QrCode payload={payment.qrPayload!} />
            <p className="text-lg font-bold">
              {t('payment.transferExactly', { amount: '' })}{' '}
              <Money amount={payment.amount.amount} currency={payment.amount.currency} />
            </p>
            {/*
              The exact-amount rule is not a formality: the cent offset is what
              lets an inbound bank notification match exactly one order, so the
              customer has to understand it.
            */}
            <p className="text-center text-xs opacity-65">{t('payment.exactAmountHint')}</p>
            {payment.payeeLabel ? (
              <p className="text-center text-sm">
                <span className="opacity-60">{t('payment.payTo')}: </span>
                {payment.payeeLabel}
              </p>
            ) : null}
            {payment.deepLink ? (
              <a href={payment.deepLink} className="btn btn-primary w-full">
                {t('payment.iHavePaid')}
              </a>
            ) : null}
            <p className="text-xs opacity-55">{t('payment.awaitingPayment')}</p>
          </section>
        ) : null}

        {order.paymentType === 'cod' && !awaitingPayment ? (
          <section className="card p-4 text-center">
            <p className="text-sm">
              {t('payment.codAmountDue', { amount: '' })}{' '}
              <Money amount={order.total.amount} currency={order.currency} className="font-bold" />
            </p>
          </section>
        ) : null}

        {/* The §6.3 handoff. Only shown when it is still worth something. */}
        {shop.botUsername && !linked && !isMiniApp() ? (
          <Button
            onClick={() => openTelegramLink(botDeepLink(shop.botUsername!, 'order', order.code))}
          >
            {t('order.getUpdatesOnTelegram')}
          </Button>
        ) : null}
        {shop.botUsername && !linked && !isMiniApp() ? (
          <p className="-mt-4 text-center text-xs opacity-60">{t('order.getUpdatesHint')}</p>
        ) : null}

        <section>
          <ol className="flex flex-col gap-3">
            {timeline.map((status, index) => {
              const done = currentIndex >= index && currentIndex !== -1;
              return (
                <li key={status} className="flex items-center gap-3">
                  <span
                    className="flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                    style={{
                      background: done ? 'var(--color-primary)' : 'var(--color-surface)',
                      color: done ? 'var(--color-primary-contrast)' : 'var(--color-text-muted)',
                    }}
                  >
                    {done ? '<Check className="inline size-4" aria-hidden />' : index + 1}
                  </span>
                  <span className={done ? 'text-sm font-medium' : 'text-sm opacity-55'}>
                    {orderStatusLabel(locale, status)}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="card p-4">
          <h2 className="mb-3 text-sm font-semibold">{t('order.items')}</h2>
          <ul className="flex flex-col gap-3">
            {order.items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="line-clamp-2">
                    {item.snapshot.productName[locale] || item.snapshot.productName.en}
                  </p>
                  {Object.values(item.snapshot.options).length > 0 ? (
                    <p className="text-xs opacity-60">
                      {Object.values(item.snapshot.options).join(' / ')} × {item.quantity}
                    </p>
                  ) : (
                    <p className="text-xs opacity-60">× {item.quantity}</p>
                  )}
                </div>
                <Money amount={item.lineTotal.amount} currency={order.currency} />
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-col gap-1.5 text-sm" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem' }}>
            <div className="flex justify-between opacity-70">
              <span>{t('common.subtotal')}</span>
              <Money amount={order.subtotal.amount} currency={order.currency} />
            </div>
            {order.shipping.amount > 0 ? (
              <div className="flex justify-between opacity-70">
                <span>{t('common.shipping')}</span>
                <Money amount={order.shipping.amount} currency={order.currency} />
              </div>
            ) : null}
            <div className="flex justify-between text-base font-bold">
              <span>{t('common.total')}</span>
              <Money amount={order.total.amount} currency={order.currency} />
            </div>
          </div>
        </section>

        {order.delivery ? (
          <section className="card p-4 text-sm">
            <h2 className="mb-2 text-sm font-semibold">{t('checkout.deliveryAddress')}</h2>
            <p>{order.delivery.receiverName}</p>
            <p className="opacity-70">{order.delivery.phone}</p>
            <p className="opacity-70">
              {provinceName(order.delivery.provinceCode, locale)}
              {order.delivery.district ? `, ${order.delivery.district}` : ''}
            </p>
            <p className="opacity-70">{order.delivery.addressLine}</p>
          </section>
        ) : null}

        <Link to="/s/$slug" params={{ slug: shop.slug }} className="btn btn-ghost w-full">
          {t('storefront.continueShopping')}
        </Link>
      </div>
    </div>
  );
}
